// ═══════════════════════════════════════════════════════════════════════════
// benchmarks — Cascadas de benchmark de margen y ACOS por publicación
// ───────────────────────────────────────────────────────────────────────────
// Resuelve el "valor de referencia" de margen sin ads / ACOS para un ítem
// puntual usando una cascada de fallbacks:
//
//   1. Capa 2 (publicación)        → más específico, usa el rollup persistido
//   2. Capa 3 (producto)           → cuando la pub no tiene data suficiente
//   3. Empresa (on-the-fly)        → promedio ponderado sobre todas las pubs
//   4. Default global              → último recurso configurable
//
// Devuelve siempre `{ valor, fuente, samples, detalle }` para trazabilidad
// en la UI (tooltip "benchmark del item: 45% — 8 órdenes" / "fallback empresa").
//
// Usa los rollups que persisten:
//   - `rentabilidad` (sync v3)         → `_rentabilidadInferida` y `_rentabilidadConsolidada`
//   - `costo-adquisicion` (sync v3)    → `_costoAdquisicionInferido` y `_costoAdquisicionConsolidado`
//
// Pure module — no hace fetch a Firestore. El caller le pasa los docs ya
// cargados en memoria.
// ═══════════════════════════════════════════════════════════════════════════

const ESTADO_RENT_CALCULADA = 'calculada'
const ESTADO_ACOS_CALCULADO = 'calculado'

const DEFAULT_MARGEN = 0.20   // 20% — fallback conservador para sellers sin histórico
const DEFAULT_ACOS = 0.15     // 15% — fallback razonable para items con ads

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// ─── Margen sin ads ────────────────────────────────────────────────────────
//
// Cascada para resolver el % de margen "limpio" del producto (sin ads):
//
//   1. mlPublicaciones._rentabilidadInferida.margenSinPubPctProm
//   2. productos._rentabilidadConsolidada.margenSinPubPctProm
//   3. promedio de la empresa (on-the-fly, desde array de pubs con _rentabilidadInferida)
//   4. defaultPct (20%)

/**
 * @param {Object} opts
 * @param {Object|null} opts.itemDoc        — doc de mlPublicaciones (con _rentabilidadInferida)
 * @param {Object|null} opts.productoDoc    — doc de productos (con _rentabilidadConsolidada)
 * @param {Array|null}  opts.publicacionesEmpresa — array de mlPublicaciones de la empresa
 *                                                   (para calcular el promedio empresa)
 * @param {number}      opts.defaultPct     — fallback final (default 0.20)
 *
 * @returns {{ valor: number, fuente: string, samples: number|null, detalle: string }}
 */
export function obtenerBenchmarkMargen({
  itemDoc = null,
  productoDoc = null,
  publicacionesEmpresa = null,
  defaultPct = DEFAULT_MARGEN,
} = {}) {
  // 1) Capa 2 — publicación
  const rentItem = itemDoc?._rentabilidadInferida
  if (rentItem?.estado === ESTADO_RENT_CALCULADA) {
    const valor = num(rentItem.margenSinPubPctProm)
    if (valor != null) {
      return {
        valor,
        fuente: 'item',
        samples: num(rentItem.unidadesVendidas),
        detalle: `Margen del item — ${rentItem.unidadesVendidas || '?'} uds históricas`,
      }
    }
  }

  // 2) Capa 3 — producto
  const rentProd = productoDoc?._rentabilidadConsolidada
  if (rentProd?.estado === ESTADO_RENT_CALCULADA) {
    const valor = num(rentProd.margenSinPubPctProm)
    if (valor != null) {
      return {
        valor,
        fuente: 'producto',
        samples: num(rentProd.unidadesTotales),
        detalle: `Margen del producto (cross-publicaciones) — ${rentProd.unidadesTotales || '?'} uds`,
      }
    }
  }

  // 3) Empresa on-the-fly: promedio ponderado por unidades sobre todas las
  //    publicaciones con _rentabilidadInferida calculada.
  if (Array.isArray(publicacionesEmpresa) && publicacionesEmpresa.length > 0) {
    const empresaMargen = calcularPromedioEmpresaMargen(publicacionesEmpresa)
    if (empresaMargen.valor != null) {
      return {
        valor: empresaMargen.valor,
        fuente: 'empresa',
        samples: empresaMargen.unidades,
        detalle: `Margen promedio de la cuenta — ${empresaMargen.unidades} uds en ${empresaMargen.publicaciones} pubs`,
      }
    }
  }

  // 4) Default
  return {
    valor: defaultPct,
    fuente: 'default',
    samples: null,
    detalle: `Default ${(defaultPct * 100).toFixed(0)}% — sin datos suficientes en la empresa`,
  }
}

/**
 * Calcula el promedio ponderado del margen sin ads sobre todas las pubs de
 * la empresa que tengan `_rentabilidadInferida.estado === 'calculada'`.
 * Ponderado por `unidadesVendidas` para que items que se mueven más pesen más.
 */
export function calcularPromedioEmpresaMargen(publicacionesEmpresa) {
  let sumaPond = 0
  let sumaUnidades = 0
  let cuentaPubs = 0
  for (const pub of publicacionesEmpresa) {
    const rent = pub?._rentabilidadInferida
    if (rent?.estado !== ESTADO_RENT_CALCULADA) continue
    const margen = num(rent.margenSinPubPctProm)
    const unidades = num(rent.unidadesVendidas)
    if (margen == null || unidades == null || unidades <= 0) continue
    sumaPond += margen * unidades
    sumaUnidades += unidades
    cuentaPubs++
  }
  return {
    valor: sumaUnidades > 0 ? sumaPond / sumaUnidades : null,
    unidades: sumaUnidades,
    publicaciones: cuentaPubs,
  }
}

// ─── ACOS ──────────────────────────────────────────────────────────────────
//
// Cascada simétrica al margen, pero usando los rollups del sync
// `costo-adquisicion`:
//
//   1. mlPublicaciones._costoAdquisicionInferido.acosProm
//   2. productos._costoAdquisicionConsolidado.acosProm
//   3. promedio de la empresa (on-the-fly)
//   4. defaultPct (15%)

/**
 * @param {Object} opts
 * @param {Object|null} opts.itemDoc        — mlPublicaciones (con _costoAdquisicionInferido)
 * @param {Object|null} opts.productoDoc    — productos (con _costoAdquisicionConsolidado)
 * @param {Array|null}  opts.publicacionesEmpresa
 * @param {number}      opts.defaultPct     — default 0.15 (15%)
 *
 * @returns {{ valor: number, fuente: string, samples: number|null, detalle: string }}
 */
export function obtenerBenchmarkAcos({
  itemDoc = null,
  productoDoc = null,
  publicacionesEmpresa = null,
  defaultPct = DEFAULT_ACOS,
} = {}) {
  // 1) Capa 2 — publicación
  const adsItem = itemDoc?._costoAdquisicionInferido
  if (adsItem?.estado === ESTADO_ACOS_CALCULADO) {
    const valor = num(adsItem.acosProm)
    if (valor != null) {
      return {
        valor,
        fuente: 'item',
        samples: num(adsItem.unidadesAtribuidas),
        detalle: `ACOS del item — ${adsItem.unidadesAtribuidas || '?'} uds atribuidas`,
      }
    }
  }

  // 2) Capa 3 — producto
  const adsProd = productoDoc?._costoAdquisicionConsolidado
  if (adsProd?.estado === ESTADO_ACOS_CALCULADO) {
    const valor = num(adsProd.acosProm)
    if (valor != null) {
      return {
        valor,
        fuente: 'producto',
        samples: num(adsProd.unidadesAtribuidas),
        detalle: `ACOS del producto — ${adsProd.unidadesAtribuidas || '?'} uds atribuidas`,
      }
    }
  }

  // 3) Empresa on-the-fly
  if (Array.isArray(publicacionesEmpresa) && publicacionesEmpresa.length > 0) {
    const empresaAcos = calcularPromedioEmpresaAcos(publicacionesEmpresa)
    if (empresaAcos.valor != null) {
      return {
        valor: empresaAcos.valor,
        fuente: 'empresa',
        samples: empresaAcos.unidades,
        detalle: `ACOS promedio de la cuenta — ${empresaAcos.unidades} uds en ${empresaAcos.publicaciones} pubs`,
      }
    }
  }

  // 4) Default
  return {
    valor: defaultPct,
    fuente: 'default',
    samples: null,
    detalle: `Default ${(defaultPct * 100).toFixed(0)}% — sin datos suficientes en la empresa`,
  }
}

/**
 * Promedio ponderado del ACOS de la empresa. ACOS no es un promedio simple
 * sino un ratio derivado, así que sumamos `costoTotal` y `totalAmountAtribuido`
 * por separado y derivamos al final.
 */
export function calcularPromedioEmpresaAcos(publicacionesEmpresa) {
  let costoTotal = 0
  let totalAmount = 0
  let unidades = 0
  let cuentaPubs = 0
  for (const pub of publicacionesEmpresa) {
    const ads = pub?._costoAdquisicionInferido
    if (ads?.estado !== ESTADO_ACOS_CALCULADO) continue
    const cost = num(ads.costoTotal)
    const amount = num(ads.totalAmountAtribuido)
    const u = num(ads.unidadesAtribuidas)
    if (cost == null || amount == null || amount <= 0) continue
    costoTotal += cost
    totalAmount += amount
    unidades += u || 0
    cuentaPubs++
  }
  return {
    valor: totalAmount > 0 ? costoTotal / totalAmount : null,
    unidades,
    publicaciones: cuentaPubs,
  }
}

// ─── Defaults exportados para que el caller pueda consultarlos ─────────────
export const DEFAULTS = Object.freeze({
  MARGEN: DEFAULT_MARGEN,
  ACOS: DEFAULT_ACOS,
})
